// Web chat and the Cockpit: sign in, then every Cockpit page in the shell (Today, Chat with LAMP, Floor, Runs (a Run's panel at `/runs/:run`),
// Documents, Customers and suppliers, Items and stock, Cash and ledgers, GST and compliance, Memory (what LAMP knows), Insights, Credits and usage, Audit log, Set up LAMP,
// Users, Roles and permissions, Connections, AI models, Settings, Plan and billing, Security, Help and System status are built; the rest are placeholders). Pages are paths, so the
// browser's back and forward buttons work; a list opened from search keeps its words in `?q=`. Memory, Credits and
// usage, Audit log, Set up LAMP, Users, Roles and permissions and Plan and billing are an Owner's; anyone else asking gets Today, as does anyone asking for a page their Roles do not open. Why LAMP's agents are paused shows above every
// page; an Owner pauses and resumes them from the sidebar or chat. The language, English or Hindi, is the
// person's, kept on their profile: switching it takes effect at once and follows them to every device. Before
// sign-in the browser's language picks (or the last one chosen on this device), and a switch made on the
// sign-in page is kept on the profile once they sign in. Settings has a Privacy tab at `/settings/privacy`, which "privacy" or "withdraw consent" in chat
// opens, and Dimension values at `/settings/dimensions`, Expense accounts at `/settings/accounts` (V-10) and Ledger Mapping at `/settings/ledger-mapping` (V-12) for whoever's Roles see them; the legal notices at `/legal/<notice>`, the sub-processor list at `/sub-processors` and the trust page at `/trust`
// are public, signed in or not, and so are signup at `/signup`, the waitlist at `/waitlist` while the Cell serves them, and
// the invitation page at `/invitation`, which an invitation email links to, a customer's quotation at `/quotation`,
// which a quotation email links to, and a customer's invoice at `/invoice`, which a payment reminder links to. Signed in from `/?oauth=…`, while
// connecting an External Assistant, it goes back to the authorization server's page.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Api, SearchResult, Session } from "./api.js";
import { AuditLog } from "./audit/AuditLog.js";
import { Chat } from "./chat/Chat.js";
import { Connections } from "./connections/Connections.js";
import { Billing } from "./billing/Billing.js";
import { createCashApi, type CashApi } from "./cash/api.js";
import { Cash } from "./cash/Cash.js";
import { useTrialDaysLeft } from "./billing/trial.js";
import { Credits } from "./credits/Credits.js";
import { Documents } from "./documents/Documents.js";
import { browserLanguage, isLanguage, type Language } from "./i18n/language.js";
import { CATALOGUES, LanguageProvider, useWords } from "./i18n/words.js";
import { Floor } from "./floor/Floor.js";
import { createGstApi, type GstApi } from "./gst/api.js";
import { Gst } from "./gst/Gst.js";
import { Help } from "./help/Help.js";
import { Insights } from "./insights/Insights.js";
import { Bills } from "./bills/Bills.js";
import { Expenses } from "./reports/expenses/Expenses.js";
import { LegalNotice, noticeAt } from "./legal/Notice.js";
import { Items } from "./items/Items.js";
import { AgentsNotice, PauseAgents, useAgents } from "./kill-switch/KillSwitch.js";
import { LicenceBanner, useLicenceBanner } from "./licence/LicenceBanner.js";
import { Memory } from "./memory/Memory.js";
import { Models } from "./models/Models.js";
import { Onboarding } from "./onboarding/Onboarding.js";
import { Parties } from "./parties/Parties.js";
import { Privacy } from "./privacy/Privacy.js";
import { SubProcessors } from "./privacy/SubProcessors.js";
import { Roles } from "./roles/Roles.js";
import { Runs, runAt } from "./runs/RunPanel.js";
import { Security } from "./security/Security.js";
import { BUILT, PAGES, mayOpen, pageAt, type Page } from "./shell/nav.js";
import { Placeholder } from "./shell/Placeholder.js";
import { Shell, type Notices } from "./shell/Shell.js";
import { ExpenseAccounts } from "./settings/accounts/ExpenseAccounts.js";
import { DimensionValues } from "./settings/dimensions/DimensionValues.js";
import { LedgerMapping } from "./settings/ledger-mapping/LedgerMapping.js";
import { Settings } from "./settings/Settings.js";
import { Quality } from "./quality/Quality.js";
import { Status } from "./status/Status.js";
import { createSignInApi, type SignInApi } from "./sign-in/api.js";
import { INVITATION_PATH, Invitation } from "./sign-in/Invitation.js";
import { createQuotationLinkApi } from "./quotation/api.js";
import { INVOICE_PATH, InvoiceLink } from "./quotation/InvoiceLink.js";
import { QUOTATION_PATH, QuotationLink } from "./quotation/QuotationLink.js";
import { oauthReturn } from "./sign-in/oauth-return.js";
import { SignIn } from "./sign-in/SignIn.js";
import { createSignupApi, type SignupApi } from "./signup/api.js";
import { ONBOARDING_PATH, SIGNUP_PATH, Signup } from "./signup/Signup.js";
import { WAITLIST_PATH, Waitlist } from "./signup/Waitlist.js";
import { Today } from "./today/Today.js";
import { TRUST_PATH } from "./trust/content.js";
import { Trust } from "./trust/Trust.js";
import { Users } from "./users/Users.js";

const OWNER_ROLE = "Owner";
/** Settings' tabs beside the business's settings, by their paths. */
const SETTINGS_TABS = {
  privacy: "/settings/privacy",
  dimensions: "/settings/dimensions",
  accounts: "/settings/accounts",
  ledgerMapping: "/settings/ledger-mapping",
} as const;
type SettingsTab = keyof typeof SETTINGS_TABS | "business";
const settingsTabAt = (path: string): SettingsTab =>
  (Object.keys(SETTINGS_TABS) as (keyof typeof SETTINGS_TABS)[]).find((tab) => SETTINGS_TABS[tab] === path) ??
  "business";
const SUB_PROCESSORS_PATH = "/sub-processors";
const LANGUAGE = "lamp.language";
const defaultSignupApi = createSignupApi();
const defaultQuotationLinkApi = createQuotationLinkApi();
const defaultSignInApi = createSignInApi();
const defaultCashApi = createCashApi();
const defaultGstApi = createGstApi();

/** The words a list was opened with, from `?q=`. */
const queryAt = (search: string) => new URLSearchParams(search).get("q") ?? "";

function readLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE);
    if (isLanguage(stored)) return stored;
  } catch {
    // Storage may be unavailable (a private window): the browser's language picks.
  }
  return browserLanguage(
    window.navigator.languages.length > 0 ? window.navigator.languages : [window.navigator.language],
  );
}

export function App({
  api,
  signup = defaultSignupApi,
  signIn = defaultSignInApi,
  cash = defaultCashApi,
  gst = defaultGstApi,
}: {
  api: Api;
  signup?: SignupApi;
  signIn?: SignInApi;
  /** What Cash and ledgers and GST and compliance read. */
  cash?: CashApi;
  gst?: GstApi;
}) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [page, setPage] = useState<Page>(() => pageAt(window.location.pathname));
  const [run, setRun] = useState<string | undefined>(() => runAt(window.location.pathname));
  const [query, setQuery] = useState(() => queryAt(window.location.search));
  const [focusWorkItem, setFocusWorkItem] = useState<string | undefined>(undefined);
  const [language, setLanguage] = useState<Language>(readLanguage);
  /** A language switched to before sign-in, to keep on the profile once signed in. */
  const chosenSignedOut = useRef<Language | undefined>(undefined);
  /** Whether the person has switched language on this page: their choice outlasts a profile read still on its way. */
  const switched = useRef(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>(() => settingsTabAt(window.location.pathname));
  /** Counts the person's navigations: Settings reads its lists again on each, so a Dimension added since shows (#678). */
  const [visit, setVisit] = useState(0);
  const visited = useCallback(() => setVisit((n) => n + 1), []);

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      window.localStorage.setItem(LANGUAGE, language);
    } catch {
      // Storage may be unavailable (a private window); the choice lasts until the page reloads.
    }
  }, [language]);

  useEffect(() => {
    api.session().then(
      (s) => setSession(s ?? null),
      () => setSession(null),
    );
  }, [api]);

  // Signed in while connecting an External Assistant: back to the authorization server's page.
  useEffect(() => {
    const back = session ? oauthReturn(window.location.search) : undefined;
    if (back !== undefined) window.location.assign(back);
  }, [session]);

  // Once signed in, the profile's language, unless the person just chose one on the sign-in page.
  const personId = session?.personId;
  useEffect(() => {
    if (personId === undefined) return;
    const chosen = chosenSignedOut.current;
    chosenSignedOut.current = undefined;
    if (chosen !== undefined) {
      api.setLanguage(chosen).catch(() => undefined);
      return;
    }
    let live = true;
    switched.current = false;
    api.me().then(
      (me) => live && !switched.current && setLanguage(me.language),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api, personId]);

  // Switching takes effect at once; signed in, it is kept on the profile too.
  const signedIn = Boolean(session);
  const switchLanguage = useCallback(
    (next: Language) => {
      switched.current = true;
      setLanguage(next);
      if (signedIn) api.setLanguage(next).catch(() => undefined);
      else chosenSignedOut.current = next;
    },
    [api, signedIn],
  );

  useEffect(() => {
    const onPop = () => {
      setPage(pageAt(window.location.pathname));
      setRun(runAt(window.location.pathname));
      setQuery(queryAt(window.location.search));
      setSettingsTab(settingsTabAt(window.location.pathname));
      visited();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [visited]);

  const navigate = useCallback(
    (next: Page, workItem?: string, words = "") => {
      const path = words ? `${PAGES[next].path}?${new URLSearchParams({ q: words }).toString()}` : PAGES[next].path;
      setFocusWorkItem(workItem);
      setRun(undefined);
      setQuery(words);
      setSettingsTab("business");
      setPage(next);
      visited();
      if (`${window.location.pathname}${window.location.search}` !== path) window.history.pushState(null, "", path);
    },
    [visited],
  );

  const openSettingsTab = useCallback(
    (tab: keyof typeof SETTINGS_TABS) => {
      setFocusWorkItem(undefined);
      setRun(undefined);
      setQuery("");
      setSettingsTab(tab);
      setPage("settings");
      visited();
      if (window.location.pathname !== SETTINGS_TABS[tab]) window.history.pushState(null, "", SETTINGS_TABS[tab]);
    },
    [visited],
  );
  const openPrivacy = useCallback(() => openSettingsTab("privacy"), [openSettingsTab]);

  const openRun = useCallback((id: string) => {
    const path = `${PAGES.runs.path}/${id}`;
    setFocusWorkItem(undefined);
    setRun(id);
    setQuery("");
    setPage("runs");
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
  }, []);

  // A search result opens where it lives: an order its Run (or the Runs searched for it), a Counterparty
  // its list; a Document opens its PDF by its own link.
  const openResult = useCallback(
    (result: SearchResult) => {
      if (result.kind === "order") {
        if (result.run) openRun(result.run);
        else navigate("runs", undefined, result.title);
      } else if (result.kind === "counterparty") navigate("parties", undefined, result.title);
      else if (!result.pdf) navigate("documents", undefined, result.title);
    },
    [navigate, openRun],
  );

  const signOut = useCallback(() => {
    void api.signOut().finally(() => setSession(null));
  }, [api]);

  const legalNotice = noticeAt(window.location.pathname);
  return (
    <LanguageProvider language={language} setLanguage={switchLanguage}>
      {legalNotice ? (
        <LegalNotice notice={legalNotice} />
      ) : window.location.pathname === SUB_PROCESSORS_PATH ? (
        <SubProcessors />
      ) : window.location.pathname === TRUST_PATH ? (
        <Trust />
      ) : window.location.pathname === SIGNUP_PATH ? (
        // Signed in, the new Owner lands in onboarding, with the page loaded afresh for the session.
        <Signup api={signup} onSignedUp={() => window.location.assign(ONBOARDING_PATH)} />
      ) : window.location.pathname === WAITLIST_PATH ? (
        <Waitlist api={signup} />
      ) : window.location.pathname === QUOTATION_PATH ? (
        <QuotationLink api={defaultQuotationLinkApi} />
      ) : window.location.pathname === INVOICE_PATH ? (
        <InvoiceLink api={defaultQuotationLinkApi} />
      ) : window.location.pathname === INVITATION_PATH ? (
        // Accepted, the person lands on Today, with the page loaded afresh for the session.
        <Invitation signIn={signIn} onAccepted={() => window.location.assign("/")} />
      ) : session === undefined ? (
        <p role="status" className="loading">
          {CATALOGUES[language].loading}
        </p>
      ) : session === null ? (
        <SignIn api={api} signIn={signIn} onSignedIn={setSession} />
      ) : (
        <Cockpit
          api={api}
          cash={cash}
          gst={gst}
          session={session}
          page={page}
          focusWorkItem={focusWorkItem}
          run={run}
          query={query}
          navigate={navigate}
          openRun={openRun}
          openResult={openResult}
          settingsTab={settingsTab}
          visit={visit}
          openSettingsTab={openSettingsTab}
          openPrivacy={openPrivacy}
          signOut={signOut}
        />
      )}
    </LanguageProvider>
  );
}

/**
 * The pages the person's Roles open (#513), read once per session: the sidebar lists only `listed`, and a typed path
 * opens any of `opens`, an empty page among them (#989). Until they are read, or if they cannot be, both are as they were.
 */
function useOpenPages(
  api: Pick<Api, "pages">,
  session: Session,
): { readonly listed: ReadonlySet<string>; readonly opens: ReadonlySet<string> } | undefined {
  const [opens, setOpens] = useState<{ listed: ReadonlySet<string>; opens: ReadonlySet<string> } | undefined>(
    undefined,
  );
  useEffect(() => {
    let live = true;
    api.pages?.().then(
      (pages) => {
        if (live) setOpens({ listed: new Set(pages.listed), opens: new Set(pages.opens) });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api, session]);
  return opens;
}

/**
 * Which of Settings' lists (V-10) the person's Roles open, read again on each navigation to or within Settings (`visit`;
 * undefined while elsewhere), so a Dimension a Tree Change added shows its tab without a reload (#678): Dimension
 * values only when the business's tree has a Dimension, Expense accounts and Ledger Mapping (V-12) when the person may see
 * them.
 */
function useSettingsLists(
  api: Pick<Api, "dimensionValues" | "expenseAccounts" | "ledgerMapping">,
  session: Session,
  visit: number | undefined,
): { readonly dimensions: boolean; readonly accounts: boolean; readonly ledgerMapping: boolean } {
  const [lists, setLists] = useState({ dimensions: false, accounts: false, ledgerMapping: false });
  useEffect(() => {
    if (visit === undefined) return;
    let live = true;
    void Promise.all([
      api.dimensionValues?.list().then(
        (values) => values.dimensions.length > 0,
        () => false,
      ) ?? false,
      api.expenseAccounts?.list().then(
        () => true,
        () => false,
      ) ?? false,
      api.ledgerMapping?.list().then(
        () => true,
        () => false,
      ) ?? false,
    ]).then(([dimensions, accounts, ledgerMapping]) => {
      if (live) setLists({ dimensions, accounts, ledgerMapping });
    });
    return () => {
      live = false;
    };
  }, [api, session, visit]);
  return lists;
}

function Cockpit({
  api,
  cash,
  gst,
  session,
  page,
  focusWorkItem,
  run,
  query,
  navigate,
  openRun,
  openResult,
  settingsTab,
  visit,
  openSettingsTab,
  openPrivacy,
  signOut,
}: {
  api: Api;
  cash: CashApi;
  gst: GstApi;
  session: Session;
  page: Page;
  focusWorkItem: string | undefined;
  run: string | undefined;
  /** The words a list page opens with. */
  query: string;
  navigate: (page: Page, workItem?: string, words?: string) => void;
  openRun: (run: string) => void;
  openResult: (result: SearchResult) => void;
  /** The Settings tab shown. */
  settingsTab: SettingsTab;
  /** Which navigation of the person's this is: a new one each time they go to a page or a Settings tab. */
  visit: number;
  openSettingsTab: (tab: keyof typeof SETTINGS_TABS) => void;
  openPrivacy: () => void;
  signOut: () => void;
}) {
  const w = useWords();
  const [agents, setAgents] = useAgents(api);
  const owner = session.roles.includes(OWNER_ROLE);
  const licence = useLicenceBanner(api, owner);
  const trialDaysLeft = useTrialDaysLeft(api, owner);
  const pages = useOpenPages(api, session);
  // A page the person may not open, typed or linked to, is Today, saying so: the page would only show its refusal
  // (#673). One they may open but the sidebar does not list, having nothing to show yet, opens empty (#989).
  const shown: Page = mayOpen(page, owner, pages?.opens) ? page : "today";
  const lists = useSettingsLists(api, session, shown === "settings" ? visit : undefined);
  // The bell lists the person's Work Items from Today, opening on its card in Chat as Today's do, and LAMP's new
  // messages in their thread (#512).
  const notices = useMemo<Notices>(
    () => ({
      load: () => api.today().then((today) => today.decisions),
      open: (decision) => navigate("chat", decision.workItem),
      messages: () => api.thread(),
      openChat: () => navigate("chat"),
      emails: {
        load: () => api.notices(),
        choose: (choice) => api.chooseNotices(choice),
        confirm: (code) => api.confirmNotices(code),
      },
    }),
    [api, navigate],
  );
  return (
    <Shell
      session={session}
      owner={owner}
      opens={pages?.listed}
      page={shown}
      onNavigate={(p) => navigate(p)}
      onSignOut={signOut}
      onSearch={(words) => api.search(words)}
      onOpenResult={openResult}
      sidebarEnd={owner ? <PauseAgents api={api} agents={agents} onChange={setAgents} /> : null}
      trialDaysLeft={trialDaysLeft}
      notices={notices}
      notice={
        <>
          <LicenceBanner banner={licence} />
          <AgentsNotice agents={agents} />
          {shown === page ? null : (
            <p className="page-refused" role="status">
              {w.pageRefused(w[PAGES[page].title])}
            </p>
          )}
        </>
      }
    >
      {!BUILT.has(shown) ? (
        <Placeholder onNavigate={(p) => navigate(p)} />
      ) : shown === "today" ? (
        <Today api={api} onOpenCard={(workItem) => navigate("chat", workItem)} onOpenRun={openRun} />
      ) : shown === "floor" ? (
        <Floor api={api} onOpenRun={openRun} />
      ) : shown === "bills" ? (
        <Bills onOpenRun={openRun} />
      ) : shown === "expenses" ? (
        <Expenses />
      ) : shown === "insights" ? (
        <Insights api={api} onOpenRun={openRun} onOpenRecord={(p, words) => navigate(p, undefined, words)} />
      ) : shown === "quality" ? (
        <Quality api={api} owner={owner} />
      ) : shown === "runs" ? (
        <Runs
          key={query}
          api={api}
          run={run}
          initialQuery={query}
          onOpenRun={openRun}
          onOpenWorkItem={(workItem) => navigate("chat", workItem)}
        />
      ) : shown === "documents" ? (
        <Documents key={query} api={api} initialQuery={query} />
      ) : shown === "parties" ? (
        <Parties key={query} api={api} initialQuery={query} />
      ) : shown === "items" ? (
        <Items key={query} api={api} initialQuery={query} />
      ) : shown === "cash" ? (
        <Cash api={cash} onOpenRun={openRun} />
      ) : shown === "gst" ? (
        <Gst api={gst} />
      ) : shown === "settings" ? (
        <>
          <div className="seg" role="group" aria-label={w.shellSettingsTabs}>
            <button type="button" aria-pressed={settingsTab === "business"} onClick={() => navigate("settings")}>
              {w.shellBusinessSettings}
            </button>
            <button type="button" aria-pressed={settingsTab === "privacy"} onClick={openPrivacy}>
              {w.shellPrivacy}
            </button>
            {lists.dimensions || settingsTab === "dimensions" ? (
              <button
                type="button"
                aria-pressed={settingsTab === "dimensions"}
                onClick={() => openSettingsTab("dimensions")}
              >
                {w.shellDimensionValues}
              </button>
            ) : null}
            {lists.accounts || settingsTab === "accounts" ? (
              <button
                type="button"
                aria-pressed={settingsTab === "accounts"}
                onClick={() => openSettingsTab("accounts")}
              >
                {w.shellExpenseAccounts}
              </button>
            ) : null}
            {lists.ledgerMapping || settingsTab === "ledgerMapping" ? (
              <button
                type="button"
                aria-pressed={settingsTab === "ledgerMapping"}
                onClick={() => openSettingsTab("ledgerMapping")}
              >
                {w.shellLedgerMapping}
              </button>
            ) : null}
          </div>
          {settingsTab === "privacy" ? (
            <Privacy api={api} owner={owner} />
          ) : settingsTab === "dimensions" && api.dimensionValues ? (
            <DimensionValues api={api.dimensionValues} />
          ) : settingsTab === "accounts" && api.expenseAccounts ? (
            <ExpenseAccounts api={api.expenseAccounts} />
          ) : settingsTab === "ledgerMapping" && api.ledgerMapping ? (
            <LedgerMapping api={api.ledgerMapping} />
          ) : (
            <Settings api={api} owner={owner} />
          )}
        </>
      ) : shown === "memory" ? (
        <Memory api={api} />
      ) : shown === "onboarding" ? (
        <Onboarding api={api} />
      ) : shown === "credits" ? (
        <Credits api={api} />
      ) : shown === "billing" ? (
        <Billing api={api} />
      ) : shown === "audit" ? (
        <AuditLog api={api} onOpenRun={openRun} />
      ) : shown === "users" ? (
        <Users api={api} />
      ) : shown === "roles" ? (
        <Roles api={api} />
      ) : shown === "connections" ? (
        <Connections api={api} personId={session.personId} />
      ) : shown === "models" ? (
        <Models api={api} />
      ) : shown === "security" ? (
        <Security api={api} personId={session.personId} />
      ) : shown === "help" ? (
        <Help api={api} onAskLamp={() => navigate("chat")} />
      ) : shown === "status" ? (
        <Status api={api} />
      ) : (
        <Chat api={api} focusWorkItem={focusWorkItem} owner={owner} onAgents={setAgents} onPrivacy={openPrivacy} />
      )}
    </Shell>
  );
}
